import { useState } from 'react';
import { X, Heart, ExternalLink } from 'lucide-react';
import { MODAL_BG } from '../assets/modalBg';
import KarmanaRegistrationForm from './KarmanaRegistrationForm';
import GoogleFormEmbed from './GoogleFormEmbed';
import { triggerGoogleFormsSync } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// fade-in from slightly above, each element staggered (used for non-Karmana path only)
const fi = (delay, dur = 0.32) => ({
  animation: `sm-fade-in ${dur}s ease ${delay}s forwards`,
  opacity: 0,
});

const dot = (delay) => ({
  animation: `sm-dot-in 0.2s ease ${delay}s forwards`,
  opacity: 0,
});

/**
 * ActivitySuccessModal
 *
 * For Karmana activities: shows a clean "You're Almost There / Register to Join"
 *   header with the registration form visible immediately (no animation delay).
 * For non-Karmana activities: shows the existing animated compact modal
 *   (You're Interested / One more step).
 *
 * Props:
 *   activityName           string
 *   registrationLink       string|null    — external link (non-Karmana)
 *   isKarmana              bool           — true = show registration form
 *   registrationFields     array          — Karmana form fields
 *   registrationRequirement string        — "mandatory"|"optional"
 *   alreadyRegistered      bool           — show "Registration Done" instead of form
 *   user                   object|null    — current user (for pre-fill)
 *   activityId             string         — for API calls
 *   onRegisterClick        fn()           — external link click
 *   onClose                fn()
 *   onRegistered           fn()           — called after Karmana form submitted
 *   onSignIn               fn(user)       — called when anonymous user signs in via OTP
 *   onOTPRequired          fn(data)       — called to hand off OTP flow
 */
export default function ActivitySuccessModal({
  activityName,
  registrationLink,
  registrationEndDate,
  isKarmana,
  registrationFull,
  registrationFields,
  registrationRequirement,
  alreadyRegistered,
  user,
  activityId,
  activitySlug,
  embedUrl = null,
  onRegisterClick,
  onClose,
  onRegistered,
  onSignIn,
  onOTPRequired,
  sourceProps = {},
  onFieldsChange,
}) {
  const [regDone, setRegDone] = useState(alreadyRegistered || false);
  const { user: authUser, openAuthModal } = useAuth();

  const handleRegSuccess = () => {
    setRegDone(true);
    if (onRegistered) onRegistered();
  };

  // Handle Google Form submission from the embedded form in this modal
  const handleFormSubmitted = async () => {
    try { await triggerGoogleFormsSync(activityId); } catch { /* 15-min cycle fallback */ }
    if (authUser) {
      toast.success('Registration submitted! Your spot will be confirmed shortly.', { duration: 5000 });
      setTimeout(() => { setRegDone(true); onRegistered?.(); }, 5000);
    } else {
      openAuthModal('form_submitted');
    }
  };

  const handleSignIn = (newUser) => {
    if (onSignIn) onSignIn(newUser);
  };

  return (
    <>
      <style>{`
        @keyframes sm-fade-in {
          from { opacity: 0; transform: translateY(-9px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes sm-dot-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes sm-line-grow {
          from { height: 0; }
          to   { height: 42px; }
        }
        @keyframes sm-line-grow-short {
          from { height: 0; }
          to   { height: 21px; }
        }
        @keyframes sm-line-fade-out {
          0%   { height: 0;    opacity: 1; }
          60%  { height: 28px; opacity: 0.5; }
          100% { height: 36px; opacity: 0; }
        }
      `}</style>

      {/* ── Overlay ── */}
      <div
        className="fixed inset-0 z-[150] bg-black/50 flex justify-center items-start px-4 pt-[72px] pb-6 overflow-y-auto"
        data-testid="activity-success-modal"
        onClick={onClose}
      >

        {/* ── Card ── */}
        <div
          className="relative shadow-xl overflow-hidden w-full max-w-sm md:max-w-[624px]"
          style={{
            backgroundColor: '#f7f5f0',
            backgroundImage: `url(${MODAL_BG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: 16,
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="success-modal-card"
        >
          {/* Close button — always visible */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-300 hover:text-gray-500 transition-colors z-10"
            data-testid="success-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          {/* ── Scrollable content ── */}
          <div className="flex-1 overflow-y-auto">

            {isKarmana ? (
              /* ── Karmana path: clean immediate form layout ── */
              <>
                {/* Header (only when form not yet done) */}
                {!regDone && (
                  <div className="text-center pt-6 pb-3 px-6">
                    <h2 className="text-2xl font-bold text-gray-900">You&apos;re Almost There</h2>
                    <p className="text-gray-700 text-base mt-1">
                      <strong>Register</strong> to Join the Activity
                    </p>
                  </div>
                )}

                {/* Form section */}
                <div className="px-6 pb-4">
                  {regDone ? (
                    <div
                      className="flex flex-col items-center gap-3 py-8 text-center"
                      data-testid="registration-done-state"
                    >
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-base font-bold text-gray-800">Registration Done!</p>
                      <p className="text-sm text-gray-500">You&apos;re all set for this activity.</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-gray-700 mb-3 text-left">
                        Provide following details to register
                      </p>
                      <KarmanaRegistrationForm
                        activityId={activityId}
                        fields={registrationFields || []}
                        user={user}
                        registrationRequirement={registrationRequirement}
                        onSuccess={handleRegSuccess}
                        onSignIn={handleSignIn}
                        onOTPRequired={onOTPRequired}
                        sourceProps={sourceProps}
                        onFieldsChange={onFieldsChange}
                        disabled={!!registrationFull}
                        registrationFull={!!registrationFull}
                      />
                    </>
                  )}
                </div>
              </>

            ) : (
              /* ── Non-Karmana path ── */
              embedUrl ? (
                /* Google Forms embed replaces the external link */
                <div className="px-3 pb-4" data-testid="success-embedded-form">
                  <div className="flex items-center justify-center gap-2 py-3" style={fi(0)}>
                    <Heart size={22} fill="#16a34a" stroke="#16a34a" />
                    <span className="text-base font-semibold text-gray-700">Register for {activityName}</span>
                  </div>
                  <GoogleFormEmbed
                    embedUrl={embedUrl}
                    onSubmitted={handleFormSubmitted}
                  />
                </div>
              ) : (
              /* Existing animated external-link layout */
              <div className="text-center py-2 px-6">

                {/* 1 — heart + "You're Interested in" */}
                <div className="flex items-center justify-center gap-2" style={fi(0)}>
                  <Heart size={22} fill="#16a34a" stroke="#16a34a" />
                  <span className="text-base font-semibold text-gray-700">You&apos;re Interested in</span>
                </div>

                {/* 2 — activity name */}
                <p
                  className="text-base font-bold text-gray-700 mt-2 px-4"
                  style={{
                    ...fi(0.2),
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                  data-testid="success-activity-name"
                >
                  {activityName}
                </p>

                {/* 3 — timeline; short (half) for no-registration, full for registration path */}
                <div className="flex justify-center my-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-600 flex-shrink-0" style={dot(0.55)} />
                    <div style={{ width: 2, backgroundColor: '#16a34a', borderRadius: 1, animation: `${registrationLink ? 'sm-line-grow' : 'sm-line-grow-short'} 0.42s ease 0.60s forwards`, height: 0 }} />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-600 flex-shrink-0" style={dot(1.0)} />
                  </div>
                </div>

                {/* 4a — registration path: "One more step" */}
                {registrationLink && (
                  <div style={fi(1.1, 0.3)}>
                    <p className="text-xl font-bold text-green-700">One more step.</p>
                    <p className="text-base text-gray-600 mt-1">
                      Complete the Organiser&apos;s Registration{' '}
                      <a
                        href={registrationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => onRegisterClick?.()}
                        className="inline-flex items-center text-blue-600 hover:text-blue-700"
                        data-testid="success-registration-link"
                      >
                        <ExternalLink size={15} />
                      </a>
                    </p>
                    {registrationEndDate && (
                      <p className="text-sm text-gray-500 mt-1.5">
                        Registration Ends on{' '}
                        <span className="font-semibold">
                          {new Date(`${registrationEndDate}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </p>
                    )}
                  </div>
                )}

                {/* 4b — no-registration path: "You're All Set" + Bring Your Friends journey */}
                {!registrationLink && (
                  <>
                    <div style={fi(1.1, 0.3)}>
                      <p className="text-xl font-bold text-green-700">You&apos;re All Set !!!</p>
                    </div>

                    {/* 5 — second short line */}
                    <div className="flex justify-center my-2">
                      <div className="flex flex-col items-center">
                        <div style={{ width: 2, backgroundColor: '#16a34a', borderRadius: 1, animation: 'sm-line-grow-short 0.42s ease 1.55s forwards', height: 0 }} />
                      </div>
                    </div>

                    {/* 6 — Bring Your Friends row (logo + text, entire row clickable) */}
                    <a
                      href={`/activities/${activitySlug || activityId}?section=journey`}
                      style={fi(2.0, 0.3)}
                      className="flex items-center justify-center gap-2 hover:opacity-80 transition-opacity"
                      data-testid="success-bring-friends-link"
                    >
                      <img src="/images/karmana-journey-logo.png" alt="Karmana Journeys" className="w-7 h-7 rounded-full object-contain flex-shrink-0" />
                      <span className="text-sm font-semibold text-gray-700">Bring Your Friends</span>
                    </a>

                    {/* 7 — fade-out tail line */}
                    <div className="flex justify-center mt-2">
                      <div className="flex flex-col items-center">
                        <div style={{ width: 2, backgroundColor: '#16a34a', borderRadius: 1, animation: 'sm-line-fade-out 0.55s ease 2.45s forwards', height: 0, opacity: 0 }} />
                      </div>
                    </div>
                  </>
                )}
              </div>
              )
            )}
          </div>

          {/* ── Sticky Karmana logo block ── */}
          <div className="flex-shrink-0 flex items-center justify-center gap-2 py-3 px-6 border-t border-gray-200/60 bg-white/60 backdrop-blur-sm">
            <img src="/images/logo.webp" alt="Karmana" className="w-7 h-7 rounded-full object-contain" />
            <div className="text-left">
              <p className="text-sm font-bold text-gray-900 leading-tight">Karmana</p>
              <p className="text-[10px] text-gray-500 leading-tight">Bengaluru&apos;s Civic Action Platform</p>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
